import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Divider,
  Drawer,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArrowsDiff,
  IconBrain,
  IconCalendarEvent,
  IconCheck,
  IconExternalLink,
  IconMessageChatbot,
  IconPencil,
  IconSparkles,
  IconX,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getKnowledgeUnit,
  patchKnowledgeUnit,
  reviewKnowledgeUnit,
  type KnowledgeDuplicateDetail,
  type KnowledgeRejectCode,
  type KnowledgeReviewAction,
  type KnowledgeUnitDetail,
  type KnowledgeUnitPatch,
} from '@mygpt/app/apis/services/knowledgeLoop';
import type { ZendeskDraftStatus } from '@mygpt/app/apis/services/zendesk';
import { charDiff, formatSimilarity, statusColor, statusLabel, type DiffSeg } from '@/components/ZendeskOps/shared';
import { ReviewChat } from './ReviewChat';
import {
  CARD_BORDER,
  REJECT_CODES,
  errorDetail,
  errorPayload,
  errorStatus,
  piiSummary,
  formatConfidence,
  formatJst,
  proposalColor,
  signalColor,
  unitStatusColor,
} from './shared';

interface Props {
  datasetId: string;
  unitId: string | null;
  onClose: () => void;
  onReviewed: () => void;
}

const DiffText = ({ segs }: { segs: DiffSeg[] }) => (
  <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.7 }}>
    {segs.map((seg, i) => (
      <span
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        style={
          seg.type === 'del'
            ? { background: '#FFE3E3', textDecoration: 'line-through', color: '#C92A2A' }
            : seg.type === 'ins'
            ? { background: '#D3F9D8', color: '#2B8A3E' }
            : undefined
        }
      >
        {seg.text}
      </span>
    ))}
  </Text>
);

// ISO/日付文字列 → <input type="date"> 用の YYYY-MM-DD（JST）
const toDateInput = (iso?: string | null): string => {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const jst = new Date(d.getTime() + 9 * 3600 * 1000);
  return `${jst.getUTCFullYear()}-${String(jst.getUTCMonth() + 1).padStart(2, '0')}-${String(jst.getUTCDate()).padStart(
    2,
    '0'
  )}`;
};

const Block = ({ title, tone, children }: { title: string; tone?: string; children: React.ReactNode }) => (
  <div style={{ border: CARD_BORDER, borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
    <div style={{ padding: '6px 12px', background: tone || '#F8F9FA', borderBottom: CARD_BORDER }}>
      <Text size="xs" fw={600} c="gray.7">
        {title}
      </Text>
    </div>
    <div style={{ padding: 12 }}>{children}</div>
  </div>
);

const Reason = ({ label, text }: { label: string; text?: string }) =>
  text ? (
    <Stack gap={2}>
      <Text size="xs" fw={600} c="gray.7">
        {label}
      </Text>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {text}
      </Text>
    </Stack>
  ) : null;

/** 審査ドロワー：提案の内容・既存との差分・証拠・「AI の根拠」を見て、承認／編集して承認／却下を決める */
export const UnitDrawer = ({ datasetId, unitId, onClose, onReviewed }: Props) => {
  const { t } = useTranslation();
  const [unit, setUnit] = useState<KnowledgeUnitDetail | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<{ title: string; body: string; similar_questions: string[] }>({
    title: '',
    body: '',
    similar_questions: [],
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<'reject' | 'withdraw' | null>(null);
  const [duplicate, setDuplicate] = useState<(KnowledgeDuplicateDetail & { action: KnowledgeReviewAction }) | null>(
    null
  );
  const [rejectCode, setRejectCode] = useState<KnowledgeRejectCode | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [chatOpen, setChatOpen] = useState(true);
  // 承認時の生効日（YYYY-MM-DD, JST）。AI が読み取った日付を既定にし、人が上書きできる
  const [effectiveFrom, setEffectiveFrom] = useState('');

  useEffect(() => {
    setUnit(null);
    setEditing(false);
    if (!unitId) return;
    let alive = true;
    getKnowledgeUnit(datasetId, unitId)
      .then(({ data }) => {
        if (!alive) return;
        setUnit(data);
        setForm({ title: data.title, body: data.body, similar_questions: data.similar_questions || [] });
        setEffectiveFrom(toDateInput(data.effective_from || data.rationale?.effective_from));
      })
      .catch((e) => {
        console.error('load unit failed:', e);
        showNotification({ color: 'red', message: t('knowledge_loop.load_error') });
      });
    return () => {
      alive = false;
    };
  }, [datasetId, unitId, t]);

  const diff = useMemo(() => {
    if (!unit?.target || unit.status !== 'candidate') return null;
    return {
      title: unit.target.title !== unit.title ? charDiff(unit.target.title, unit.title) : null,
      body: charDiff(unit.target.body, unit.body),
    };
  }, [unit]);

  const edited = useMemo<KnowledgeUnitPatch | null>(() => {
    if (!unit) return null;
    const patch: KnowledgeUnitPatch = {};
    if (form.title.trim() !== unit.title) patch.title = form.title.trim();
    if (form.body.trim() !== unit.body) patch.body = form.body.trim();
    if (JSON.stringify(form.similar_questions) !== JSON.stringify(unit.similar_questions || []))
      patch.similar_questions = form.similar_questions;
    return Object.keys(patch).length ? patch : null;
  }, [form, unit]);

  const done = (message: string) => {
    showNotification({ color: 'green', message });
    onReviewed();
    onClose();
  };

  const act = async (action: KnowledgeReviewAction, force = false) => {
    if (!unit) return;
    setBusy(action);
    try {
      // 編集しながらの承認（競合で「新しい内容を採用」も同じ）は approve_with_edit で稿ごと送る
      const withEdit = editing && !!edited && (action === 'approve' || action === 'adopt_new');
      const { data } = await reviewKnowledgeUnit(datasetId, unit.id, {
        action: withEdit ? 'approve_with_edit' : action,
        patch: withEdit ? edited || undefined : undefined,
        effective_from: effectiveFrom || undefined,
        force: force || undefined,
      });
      setDuplicate(null);
      done(
        data.unit?.status === 'scheduled'
          ? t('knowledge_loop.review.done_scheduled', { date: effectiveFrom })
          : t(`knowledge_loop.review.done_${action}`)
      );
    } catch (e: any) {
      // 承認時の重複警告：公開中 / 生効待ちにほぼ同じ知識がある → 確認してから押し切る
      const detail = errorPayload(e);
      if (errorStatus(e) === 409 && detail?.code === 'duplicate') {
        setDuplicate({ ...(detail as KnowledgeDuplicateDetail), action });
        return;
      }
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.review.error') });
    } finally {
      setBusy(null);
    }
  };

  const submitReject = async () => {
    if (!unit || !rejecting || !rejectCode) return;
    setBusy(rejecting);
    try {
      await reviewKnowledgeUnit(datasetId, unit.id, {
        action: rejecting,
        reject_code: rejectCode,
        reject_note: rejectNote.trim() || undefined,
      });
      setRejecting(null);
      done(t(`knowledge_loop.review.done_${rejecting}`));
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.review.error') });
    } finally {
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    if (!unit || !edited) return;
    setBusy('save');
    try {
      const { data } = await patchKnowledgeUnit(datasetId, unit.id, edited);
      setUnit(data);
      setEditing(false);
      showNotification({ color: 'green', message: t('knowledge_loop.review.saved') });
      onReviewed();
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setBusy(null);
    }
  };

  // AI 対話で修正が適用された／台帳側が変わったとき：表示と編集フォームを最新に揃える
  const onUnitChanged = (next: KnowledgeUnitDetail) => {
    setUnit(next);
    setForm({ title: next.title, body: next.body, similar_questions: next.similar_questions || [] });
    setEditing(false);
    onReviewed();
  };

  const r = unit?.rationale || {};
  const isCandidate = unit?.status === 'candidate';
  const isConflict = unit?.proposal_type === 'conflict';

  return (
    <Drawer
      opened={!!unitId}
      onClose={onClose}
      position="right"
      size={unit && chatOpen ? 1140 : 720}
      styles={{
        content: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        body: { flex: 1, minHeight: 0, padding: 0, display: 'flex' },
      }}
      title={
        unit && (
          <Group gap={8}>
            <Badge variant="light" color={proposalColor(unit.proposal_type)}>
              {t(`knowledge_loop.proposal.${unit.proposal_type}`)}
            </Badge>
            <Badge variant="dot" color={unitStatusColor(unit.status)}>
              {t(`knowledge_loop.unit_status.${unit.status}`)}
            </Badge>
            <Badge variant="outline" color="gray">
              {t(`knowledge_loop.form.${unit.publish_form}`)}
            </Badge>
            {!!r.pii_masked && (
              <Tooltip label={t('knowledge_loop.pii.masked_hint')} multiline w={280} withArrow>
                <Badge variant="light" color="orange" style={{ textTransform: 'none' }}>
                  {t('knowledge_loop.pii.masked_chip', { summary: piiSummary(r.pii_masked) })}
                </Badge>
              </Tooltip>
            )}
            {!!r.pii_flagged && (
              <Tooltip label={t('knowledge_loop.pii.flagged_hint')} multiline w={280} withArrow>
                <Badge variant="light" color="red" style={{ textTransform: 'none' }}>
                  {t('knowledge_loop.pii.flagged_chip', { summary: piiSummary(r.pii_flagged) })}
                </Badge>
              </Tooltip>
            )}
            <Text size="xs" c="gray.6">
              {unit.status === 'approved' || unit.status === 'merged' || unit.status === 'superseded'
                ? `v${unit.version}`
                : t('knowledge_loop.review.revision_count', { count: unit.draft_revision || 0 })}
              {unit.status === 'scheduled' && unit.effective_from
                ? ` ・ ${t('knowledge_loop.review.scheduled_for', { date: toDateInput(unit.effective_from) })}`
                : ''}{' '}
              ・ {t('knowledge_loop.review.confidence')} {formatConfidence(unit.confidence)} ・{' '}
              {t('knowledge_loop.review.evidence_count', { count: unit.evidence_count })}
            </Text>
          </Group>
        )
      }
    >
      {!unit ? (
        <Group justify="center" py={80} w="100%">
          <Loader size="sm" />
        </Group>
      ) : (
        <div style={{ display: 'flex', width: '100%', minHeight: 0 }}>
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
              <Stack gap="md">
                {editing ? (
                  <Stack gap="sm">
                    <TextInput
                      label={t('knowledge_loop.review.field_title')}
                      value={form.title}
                      onChange={(e) => setForm((f) => ({ ...f, title: e.currentTarget.value }))}
                    />
                    <Textarea
                      label={t('knowledge_loop.review.field_body')}
                      autosize
                      minRows={6}
                      maxRows={18}
                      value={form.body}
                      onChange={(e) => setForm((f) => ({ ...f, body: e.currentTarget.value }))}
                    />
                    <TagsInput
                      label={t('knowledge_loop.review.field_similar')}
                      value={form.similar_questions}
                      onChange={(v) => setForm((f) => ({ ...f, similar_questions: v.slice(0, 5) }))}
                    />
                  </Stack>
                ) : diff ? (
                  <Stack gap="sm">
                    <Block
                      title={t(
                        unit.target?.type === 'faq'
                          ? 'knowledge_loop.review.existing_faq'
                          : 'knowledge_loop.review.existing_unit'
                      )}
                      tone="#FFF5F5"
                    >
                      {diff.title && <DiffText segs={diff.title.draft} />}
                      {!diff.title && (
                        <Text size="sm" fw={600} mb={6}>
                          {unit.target?.title}
                        </Text>
                      )}
                      <DiffText segs={diff.body.draft} />
                    </Block>
                    <Block title={t('knowledge_loop.review.proposed')} tone="#EBFBEE">
                      {diff.title ? (
                        <DiffText segs={diff.title.final} />
                      ) : (
                        <Text size="sm" fw={600} mb={6}>
                          {unit.title}
                        </Text>
                      )}
                      <DiffText segs={diff.body.final} />
                    </Block>
                  </Stack>
                ) : (
                  <Block title={t(`knowledge_loop.kind.${unit.kind}`)}>
                    <Text fw={600} mb={8}>
                      {unit.title}
                    </Text>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                      {unit.body}
                    </Text>
                  </Block>
                )}

                {!editing && !!unit.similar_questions?.length && (
                  <Group gap={6}>
                    <Text size="xs" c="gray.6">
                      {t('knowledge_loop.review.field_similar')}
                    </Text>
                    {unit.similar_questions.map((q) => (
                      <Badge key={q} variant="light" color="gray" size="sm" style={{ textTransform: 'none' }}>
                        {q}
                      </Badge>
                    ))}
                  </Group>
                )}

                <Block title={t('knowledge_loop.rationale.title')} tone="#F3F0FF">
                  {r.backfilled ? (
                    <Text size="sm" c="gray.6">
                      {t('knowledge_loop.rationale.backfilled')}
                    </Text>
                  ) : (
                    <Stack gap="sm">
                      <Group gap={6}>
                        <IconSparkles size={14} color="#7048E8" />
                        {(r.signals || []).map((s) => (
                          <Badge key={s} size="xs" variant="light" color={signalColor(s)}>
                            {t(`knowledge_loop.signal.${s}`)}
                          </Badge>
                        ))}
                        {(r.edit_classes || []).map((c) => (
                          <Badge key={c} size="xs" variant="outline" color="violet">
                            {t(`knowledge_loop.edit_class.${c}`, c)}
                          </Badge>
                        ))}
                      </Group>
                      <Reason label={t('knowledge_loop.rationale.why_extracted')} text={r.why_extracted} />
                      <Reason label={t('knowledge_loop.rationale.generalizability')} text={r.generalizability} />
                      <Reason
                        label={t('knowledge_loop.rationale.merge', {
                          decision: t(`knowledge_loop.proposal.${unit.proposal_type}`),
                        })}
                        text={r.merge_reason || r.change_summary}
                      />
                      <Reason
                        label={t('knowledge_loop.rationale.form', {
                          form: t(`knowledge_loop.form.${r.form_decision || unit.publish_form}`),
                        })}
                        text={r.form_reason}
                      />
                      {!!r.fact_changes?.length && (
                        <Stack gap={4}>
                          <Group gap={4}>
                            <IconArrowsDiff size={13} color="#E8590C" />
                            <Text size="xs" fw={600} c="gray.7">
                              {t('knowledge_loop.rationale.fact_changes')}
                            </Text>
                            {r.change_kind && (
                              <Badge size="xs" variant="light" color="orange">
                                {t(`knowledge_loop.change_kind.${r.change_kind}`, r.change_kind)}
                              </Badge>
                            )}
                          </Group>
                          <Table withTableBorder withColumnBorders verticalSpacing={2} horizontalSpacing="xs" fz="xs">
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>{t('knowledge_loop.rationale.fc_item')}</Table.Th>
                                <Table.Th>{t('knowledge_loop.rationale.fc_old')}</Table.Th>
                                <Table.Th>{t('knowledge_loop.rationale.fc_new')}</Table.Th>
                                <Table.Th>{t('knowledge_loop.rationale.fc_basis')}</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {r.fact_changes.map((fc, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Table.Tr key={i}>
                                  <Table.Td>{fc.item}</Table.Td>
                                  <Table.Td style={{ color: '#C92A2A' }}>{fc.old || '—'}</Table.Td>
                                  <Table.Td style={{ color: '#2B8A3E' }}>{fc.new || '—'}</Table.Td>
                                  <Table.Td c="gray.6">{fc.basis || '—'}</Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </Stack>
                      )}
                      {(r.effective_from || unit.effective_from) && (
                        <Reason
                          label={t('knowledge_loop.rationale.effective_from')}
                          text={toDateInput(unit.effective_from || r.effective_from)}
                        />
                      )}
                      {!!r.contradictions?.length && (
                        <Alert
                          color="red"
                          variant="light"
                          icon={<IconAlertTriangle size={16} />}
                          title={t('knowledge_loop.rationale.contradictions')}
                        >
                          {r.contradictions.map((c) => (
                            <Text key={c} size="xs">
                              ・{c}
                            </Text>
                          ))}
                        </Alert>
                      )}
                      {!!r.evidence_quotes?.length && (
                        <Stack gap={4}>
                          <Text size="xs" fw={600} c="gray.7">
                            {t('knowledge_loop.rationale.quotes')}
                            <Text span size="xs" c="gray.5" fw={400}>
                              {' '}
                              {t('knowledge_loop.rationale.quotes_basis')}
                            </Text>
                          </Text>
                          {r.evidence_quotes.map((q, i) => (
                            <Group key={`${q.source_ref}-${i}`} gap={8} wrap="nowrap" align="flex-start">
                              <Badge size="xs" variant="outline" color="gray" style={{ flexShrink: 0 }}>
                                {q.source_ref || '—'}
                              </Badge>
                              <Text size="xs" c="gray.8" style={{ borderLeft: '2px solid #D0BFFF', paddingLeft: 8 }}>
                                {q.quote}
                              </Text>
                            </Group>
                          ))}
                        </Stack>
                      )}
                      {!!r.memories_applied?.length && (
                        <Stack gap={4}>
                          <Group gap={4}>
                            <IconBrain size={13} color="#868E96" />
                            <Text size="xs" fw={600} c="gray.7">
                              {t('knowledge_loop.rationale.memories')}
                            </Text>
                          </Group>
                          {r.memories_applied.map((m) => (
                            <Text key={m.memory_id} size="xs" c="gray.7">
                              ・{m.text}
                            </Text>
                          ))}
                        </Stack>
                      )}
                      {!!r.uncertainties?.length && (
                        <Alert
                          color="yellow"
                          variant="light"
                          icon={<IconAlertTriangle size={16} />}
                          title={t('knowledge_loop.rationale.uncertainties')}
                        >
                          {r.uncertainties.map((u) => (
                            <Text key={u} size="xs">
                              ・{u}
                            </Text>
                          ))}
                        </Alert>
                      )}
                    </Stack>
                  )}
                </Block>

                <Stack gap={6}>
                  <Text size="sm" fw={600}>
                    {t('knowledge_loop.review.evidence', { count: unit.evidence.length })}
                  </Text>
                  {unit.evidence.length === 0 ? (
                    <Text size="xs" c="gray.5">
                      {t('knowledge_loop.review.no_evidence')}
                    </Text>
                  ) : (
                    <Accordion variant="separated" radius="md" chevronPosition="left">
                      {unit.evidence.map((ev) => (
                        <Accordion.Item key={ev.id} value={ev.id}>
                          <Accordion.Control>
                            <Group gap={8} wrap="nowrap">
                              <Badge size="xs" variant="light" color={signalColor(ev.kind)} style={{ flexShrink: 0 }}>
                                {t(`knowledge_loop.signal.${ev.kind}`, ev.kind)}
                              </Badge>
                              <Text size="xs" fw={500} style={{ flexShrink: 0 }}>
                                {ev.source_ref}
                              </Text>
                              <Text size="xs" c="gray.6" lineClamp={1}>
                                {ev.subject || ev.quote || ''}
                              </Text>
                            </Group>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Text size="xs" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                              {ev.content || ev.quote || '—'}
                            </Text>
                            {ev.url && (
                              <Anchor
                                href={ev.url}
                                target="_blank"
                                rel="noreferrer"
                                size="xs"
                                mt={6}
                                display="inline-flex"
                              >
                                <Group gap={4}>
                                  {t('knowledge_loop.review.open_ticket')}
                                  <IconExternalLink size={12} />
                                </Group>
                              </Anchor>
                            )}
                          </Accordion.Panel>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  )}
                </Stack>

                {unit.usage && ['approved', 'scheduled', 'merged', 'superseded', 'stale'].includes(unit.status) && (
                  <Stack gap={6}>
                    <Text size="sm" fw={600}>
                      {t('knowledge_loop.review.usage_title', { days: unit.usage.days })}
                    </Text>
                    {unit.usage.drafts === 0 ? (
                      <Text size="xs" c="gray.5">
                        {t('knowledge_loop.review.usage_empty')}
                      </Text>
                    ) : (
                      <>
                        <Text size="xs" c="gray.7">
                          {t('knowledge_loop.review.usage_line', {
                            drafts: unit.usage.drafts,
                            as_is: unit.usage.as_is,
                            edited: unit.usage.edited,
                            self: unit.usage.self,
                          })}
                        </Text>
                        {unit.usage.recent.map((r) => (
                          <Group key={`${r.ticket_id}-${r.draft_version}`} gap={8} wrap="nowrap">
                            <Text size="xs" fw={600} style={{ fontFamily: 'monospace', flexShrink: 0 }}>
                              #{r.ticket_id}
                              {r.draft_version > 1 ? ` v${r.draft_version}` : ''}
                            </Text>
                            <Badge
                              size="xs"
                              variant="light"
                              color={statusColor(r.status as ZendeskDraftStatus)}
                              style={{ textTransform: 'none', flexShrink: 0 }}
                            >
                              {statusLabel(t, r.status as ZendeskDraftStatus)}
                            </Badge>
                            <Text size="xs" c="gray.6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {formatSimilarity(r.similarity)}
                            </Text>
                            {r.unit_version ? (
                              <Text size="xs" c="gray.5">
                                {t('knowledge_loop.review.usage_unit_version', { version: r.unit_version })}
                              </Text>
                            ) : null}
                            <Text size="xs" c="gray.5" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                              {formatJst(r.answered_at || r.created_at)}
                            </Text>
                          </Group>
                        ))}
                      </>
                    )}
                  </Stack>
                )}

                {unit.revisions.length > 0 && (
                  <Stack gap={6}>
                    <Text size="sm" fw={600}>
                      {t('knowledge_loop.review.revisions')}
                    </Text>
                    {unit.revisions.map((rev) => (
                      <Group key={`${rev.version}-${rev.created_at}`} gap={8} wrap="nowrap" align="flex-start">
                        <Badge size="xs" variant="outline" color="gray" style={{ flexShrink: 0 }}>
                          {rev.revision_no
                            ? t('knowledge_loop.review.revision_no', { n: rev.revision_no })
                            : `v${rev.version}`}
                        </Badge>
                        <Stack gap={0} style={{ flex: 1 }}>
                          <Text size="xs" c="gray.6">
                            {formatJst(rev.created_at)} ・{' '}
                            {t(`knowledge_loop.revision_source.${rev.source}`, rev.source)}
                            {rev.author ? ` ・ ${rev.author}` : ''}
                          </Text>
                          <Text size="xs" lineClamp={2}>
                            {rev.change_summary || rev.body}
                          </Text>
                        </Stack>
                      </Group>
                    ))}
                  </Stack>
                )}

                {unit.status === 'rejected' && (
                  <Alert color="gray" variant="light">
                    <Text size="xs">
                      {t(`knowledge_loop.reject_code.${unit.reject_code || 'other'}`)}
                      {unit.reject_note ? ` — ${unit.reject_note}` : ''}
                    </Text>
                  </Alert>
                )}
              </Stack>
            </div>
            <div style={{ background: '#fff', borderTop: CARD_BORDER, padding: '12px 16px', flexShrink: 0 }}>
              <Group justify="space-between">
                <Group gap={8}>
                  <Button
                    size="xs"
                    variant={chatOpen ? 'filled' : 'light'}
                    color="violet"
                    leftSection={<IconMessageChatbot size={14} />}
                    onClick={() => setChatOpen((v) => !v)}
                  >
                    {t('knowledge_loop.chat.toggle')}
                  </Button>
                  {(isCandidate || unit.status === 'approved' || unit.status === 'scheduled') &&
                    (editing ? (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => {
                          setEditing(false);
                          setForm({
                            title: unit.title,
                            body: unit.body,
                            similar_questions: unit.similar_questions || [],
                          });
                        }}
                      >
                        {t('knowledge_loop.review.cancel_edit')}
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPencil size={14} />}
                        onClick={() => setEditing(true)}
                      >
                        {t('knowledge_loop.review.edit')}
                      </Button>
                    ))}
                </Group>
                <Group gap={8}>
                  {isCandidate && (
                    <Tooltip label={t('knowledge_loop.review.effective_from_hint')} withArrow multiline w={280}>
                      <TextInput
                        size="xs"
                        type="date"
                        w={150}
                        leftSection={<IconCalendarEvent size={14} />}
                        value={effectiveFrom}
                        onChange={(e) => setEffectiveFrom(e.currentTarget.value)}
                        aria-label={t('knowledge_loop.review.effective_from')}
                      />
                    </Tooltip>
                  )}
                  {isCandidate && (
                    <>
                      <Button
                        size="xs"
                        variant="default"
                        leftSection={<IconX size={14} />}
                        disabled={!!busy}
                        onClick={() => (isConflict ? act('keep_existing') : setRejecting('reject'))}
                        loading={busy === 'keep_existing'}
                      >
                        {isConflict ? t('knowledge_loop.review.keep_existing') : t('knowledge_loop.review.reject')}
                      </Button>
                      <Button
                        size="xs"
                        color="green"
                        leftSection={<IconCheck size={14} />}
                        disabled={!!busy && busy !== 'approve' && busy !== 'adopt_new'}
                        loading={busy === 'approve' || busy === 'adopt_new'}
                        onClick={() => act(isConflict ? 'adopt_new' : 'approve')}
                      >
                        {isConflict
                          ? t('knowledge_loop.review.adopt_new')
                          : editing && edited
                          ? t('knowledge_loop.review.approve_with_edit')
                          : t('knowledge_loop.review.approve')}
                      </Button>
                    </>
                  )}
                  {unit.status === 'scheduled' && !editing && (
                    <Button size="xs" variant="default" color="red" onClick={() => setRejecting('withdraw')}>
                      {t('knowledge_loop.review.withdraw')}
                    </Button>
                  )}
                  {unit.status === 'approved' &&
                    (editing ? (
                      <Button size="xs" disabled={!edited} loading={busy === 'save'} onClick={saveEdit}>
                        {t('knowledge_loop.review.save_published')}
                      </Button>
                    ) : (
                      <Button size="xs" variant="default" color="red" onClick={() => setRejecting('withdraw')}>
                        {t('knowledge_loop.review.withdraw')}
                      </Button>
                    ))}
                </Group>
              </Group>
            </div>
          </div>
          {chatOpen && (
            <div style={{ width: 420, flexShrink: 0, borderLeft: CARD_BORDER, minHeight: 0 }}>
              <ReviewChat datasetId={datasetId} unit={unit} onUnitChanged={onUnitChanged} />
            </div>
          )}
        </div>
      )}

      <Modal
        opened={!!duplicate}
        onClose={() => setDuplicate(null)}
        centered
        size="sm"
        title={t('knowledge_loop.review.duplicate_title')}
      >
        {duplicate && (
          <Stack gap="sm">
            <Text size="sm">
              {t('knowledge_loop.review.duplicate_desc', {
                title: duplicate.title,
                status: t(`knowledge_loop.unit_status.${duplicate.status}`),
                score: Math.round(duplicate.score * 100),
              })}
            </Text>
            <Text size="xs" c="gray.6">
              {t('knowledge_loop.review.duplicate_hint')}
            </Text>
            <Group justify="flex-end" gap={8}>
              <Button size="xs" variant="default" onClick={() => setDuplicate(null)}>
                {t('knowledge_loop.cancel')}
              </Button>
              <Button size="xs" color="orange" loading={!!busy} onClick={() => act(duplicate.action, true)}>
                {t('knowledge_loop.review.duplicate_force')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={!!rejecting}
        onClose={() => setRejecting(null)}
        centered
        size="sm"
        title={t(rejecting === 'withdraw' ? 'knowledge_loop.review.withdraw' : 'knowledge_loop.review.reject')}
      >
        <Stack gap="sm">
          <Text size="xs" c="gray.6">
            {t('knowledge_loop.review.reject_desc')}
          </Text>
          <Select
            label={t('knowledge_loop.review.reject_reason')}
            withAsterisk
            data={REJECT_CODES.map((c) => ({ value: c, label: t(`knowledge_loop.reject_code.${c}`) }))}
            value={rejectCode}
            onChange={(v) => setRejectCode(v as KnowledgeRejectCode | null)}
          />
          <Textarea
            label={t('knowledge_loop.review.reject_note')}
            autosize
            minRows={2}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.currentTarget.value)}
          />
          <Divider />
          <Group justify="flex-end" gap={8}>
            <Button size="xs" variant="default" onClick={() => setRejecting(null)}>
              {t('alert_cancel')}
            </Button>
            <Button size="xs" color="red" disabled={!rejectCode} loading={!!busy} onClick={submitReject}>
              {t(rejecting === 'withdraw' ? 'knowledge_loop.review.withdraw' : 'knowledge_loop.review.reject')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Drawer>
  );
};
